/**
 * Equipment Redux Slice
 *
 * Manages state for:
 * - Units (Trucks, Wireline Reels, Cables)
 * - Panels (Data Acquisition, Conditional)
 * - Pressure Equipment (BOP, Chokes, Valves)
 * - Consumables (Cables, Lubricants, Seals)
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import * as Types from '@/types'
import { apiClient } from '@/services/api'

// Types
interface EquipmentState {
  units: {
    items: Types.Unit[]
    total: number
    page: number
    pageSize: number
    filters: Record<string, any>
    isLoading: boolean
    error: string | null
  }
  panels: {
    items: Types.Panel[]
    total: number
    page: number
    pageSize: number
    filters: Record<string, any>
    isLoading: boolean
    error: string | null
  }
  pressureEquipment: {
    items: Types.PressureEquipment[]
    total: number
    page: number
    pageSize: number
    filters: Record<string, any>
    isLoading: boolean
    error: string | null
  }
  consumables: {
    items: Types.Consumable[]
    total: number
    page: number
    pageSize: number
    filters: Record<string, any>
    isLoading: boolean
    error: string | null
  }
}

interface FetchParams {
  skip?: number
  limit?: number
  filters?: Record<string, any>
}

// Initial state
const initialState: EquipmentState = {
  units: {
    items: [],
    total: 0,
    page: 0,
    pageSize: 10,
    filters: {},
    isLoading: false,
    error: null,
  },
  panels: {
    items: [],
    total: 0,
    page: 0,
    pageSize: 10,
    filters: {},
    isLoading: false,
    error: null,
  },
  pressureEquipment: {
    items: [],
    total: 0,
    page: 0,
    pageSize: 10,
    filters: {},
    isLoading: false,
    error: null,
  },
  consumables: {
    items: [],
    total: 0,
    page: 0,
    pageSize: 10,
    filters: {},
    isLoading: false,
    error: null,
  },
}

// Async thunks
export const fetchUnits = createAsyncThunk(
  'equipment/fetchUnits',
  async (params: FetchParams = {}, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/api/v1/equipment/units', {
        params: {
          skip: params.skip || 0,
          limit: params.limit || 100,
          ...params.filters,
        },
      })
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch units')
    }
  }
)

export const fetchPanels = createAsyncThunk(
  'equipment/fetchPanels',
  async (params: FetchParams = {}, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/api/v1/equipment/panels', {
        params: {
          skip: params.skip || 0,
          limit: params.limit || 100,
          ...params.filters,
        },
      })
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch panels')
    }
  }
)

export const fetchPressureEquipment = createAsyncThunk(
  'equipment/fetchPressureEquipment',
  async (params: FetchParams = {}, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/api/v1/equipment/pressure-equipment', {
        params: {
          skip: params.skip || 0,
          limit: params.limit || 100,
          ...params.filters,
        },
      })
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch pressure equipment')
    }
  }
)

export const fetchConsumables = createAsyncThunk(
  'equipment/fetchConsumables',
  async (params: FetchParams = {}, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/api/v1/equipment/consumables', {
        params: {
          skip: params.skip || 0,
          limit: params.limit || 100,
          ...params.filters,
        },
      })
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch consumables')
    }
  }
)

export const fetchUnit = createAsyncThunk(
  'equipment/fetchUnit',
  async (unitId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/api/v1/equipment/units/${unitId}`)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch unit')
    }
  }
)

export const fetchPanel = createAsyncThunk(
  'equipment/fetchPanel',
  async (panelId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/api/v1/equipment/panels/${panelId}`)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch panel')
    }
  }
)

export const fetchPressureEquipmentDetail = createAsyncThunk(
  'equipment/fetchPressureEquipmentDetail',
  async (equipmentId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/api/v1/equipment/pressure-equipment/${equipmentId}`)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch pressure equipment')
    }
  }
)

export const fetchConsumable = createAsyncThunk(
  'equipment/fetchConsumable',
  async (consumableId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/api/v1/equipment/consumables/${consumableId}`)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch consumable')
    }
  }
)

// Slice
const equipmentSlice = createSlice({
  name: 'equipment',
  initialState,
  reducers: {
    clearEquipmentError: (state) => {
      state.units.error = null
      state.panels.error = null
      state.pressureEquipment.error = null
      state.consumables.error = null
    },
  },
  extraReducers: (builder) => {
    // Fetch Units
    builder
      .addCase(fetchUnits.pending, (state) => {
        state.units.isLoading = true
        state.units.error = null
      })
      .addCase(fetchUnits.fulfilled, (state, action) => {
        state.units.isLoading = false
        state.units.items = action.payload.items || []
        state.units.total = action.payload.total || 0
        state.units.page = action.payload.page || 0
        state.units.pageSize = action.payload.page_size || 10
      })
      .addCase(fetchUnits.rejected, (state, action) => {
        state.units.isLoading = false
        state.units.error = action.payload as string
      })

    // Fetch Panels
    builder
      .addCase(fetchPanels.pending, (state) => {
        state.panels.isLoading = true
        state.panels.error = null
      })
      .addCase(fetchPanels.fulfilled, (state, action) => {
        state.panels.isLoading = false
        state.panels.items = action.payload.items || []
        state.panels.total = action.payload.total || 0
        state.panels.page = action.payload.page || 0
        state.panels.pageSize = action.payload.page_size || 10
      })
      .addCase(fetchPanels.rejected, (state, action) => {
        state.panels.isLoading = false
        state.panels.error = action.payload as string
      })

    // Fetch Pressure Equipment
    builder
      .addCase(fetchPressureEquipment.pending, (state) => {
        state.pressureEquipment.isLoading = true
        state.pressureEquipment.error = null
      })
      .addCase(fetchPressureEquipment.fulfilled, (state, action) => {
        state.pressureEquipment.isLoading = false
        state.pressureEquipment.items = action.payload.items || []
        state.pressureEquipment.total = action.payload.total || 0
        state.pressureEquipment.page = action.payload.page || 0
        state.pressureEquipment.pageSize = action.payload.page_size || 10
      })
      .addCase(fetchPressureEquipment.rejected, (state, action) => {
        state.pressureEquipment.isLoading = false
        state.pressureEquipment.error = action.payload as string
      })

    // Fetch Consumables
    builder
      .addCase(fetchConsumables.pending, (state) => {
        state.consumables.isLoading = true
        state.consumables.error = null
      })
      .addCase(fetchConsumables.fulfilled, (state, action) => {
        state.consumables.isLoading = false
        state.consumables.items = action.payload.items || []
        state.consumables.total = action.payload.total || 0
        state.consumables.page = action.payload.page || 0
        state.consumables.pageSize = action.payload.page_size || 10
      })
      .addCase(fetchConsumables.rejected, (state, action) => {
        state.consumables.isLoading = false
        state.consumables.error = action.payload as string
      })
  },
})

export const { clearEquipmentError } = equipmentSlice.actions
export default equipmentSlice.reducer
